import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { API_BASE_URL } from "@/services/config";

const IS_DEV = import.meta.env.DEV;

// Create axios instance with default configuration
const api = Axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies in requests
  adapter: typeof window === "undefined" ? undefined : (["xhr", "fetch"] as const),
});

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const cookies = document.cookie?.split(";") ?? [];

  for (const cookie of cookies) {
    const [key, ...rest] = cookie.trim().split("=");
    if (key === name) {
      return decodeURIComponent(rest.join("="));
    }
  }

  return null;
};

// Request interceptor for auth tokens, request logging, etc.
api.interceptors.request.use(
  (config) => {
    // When using FormData, delete Content-Type so Axios auto-generates
    // the multipart boundary. Without this, the server can't parse the request.
    if (config.data instanceof FormData && config.headers) {
      config.headers.delete("Content-Type");
    }

    // We're using cookie-based auth, so no need to add Bearer token
    if (config.method && ["post", "put", "patch", "delete"].includes(config.method.toLowerCase())) {
      const csrfToken = getCookieValue("XSRF-TOKEN");
      if (csrfToken && config.headers) {
        config.headers.set("X-CSRF-TOKEN", csrfToken);
      }
    }

    // Log requests in development
    if (IS_DEV) {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    if (IS_DEV) {
      console.error("❌ API Request Error:", error);
    }
    return Promise.reject(error);
  },
);

// Credential-bearing response fields that must never reach the console. The
// email-verification continuation (pending authentication token, verification
// ID) is an ephemeral WorkOS credential carried on 401 error payloads.
const SENSITIVE_PAYLOAD_KEYS = new Set([
  "pendingauthenticationtoken",
  "emailverificationid",
  "code",
  "email",
  "password",
  "token",
]);

/**
 * Deep-copies a response payload with credential-bearing fields masked so
 * development diagnostics can never leak secrets to the console or any
 * console-forwarding telemetry.
 */
const sanitizePayloadForLogging = (payload: unknown): unknown => {
  if (Array.isArray(payload)) {
    return payload.map(sanitizePayloadForLogging);
  }

  if (payload !== null && typeof payload === "object") {
    return Object.fromEntries(
      Object.entries(payload as Record<string, unknown>).map(([key, value]) =>
        SENSITIVE_PAYLOAD_KEYS.has(key.toLowerCase())
          ? [key, "[REDACTED]"]
          : [key, sanitizePayloadForLogging(value)],
      ),
    );
  }

  return payload;
};

// Response interceptor for error handling, response logging, etc.
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (IS_DEV) {
      console.log(
        `✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      );
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const statusText = error.response?.statusText ?? error.message;
    const payload = error.response?.data;

    if (IS_DEV) {
      const logPrefix = `❌ API Response Error: ${error.config?.method?.toUpperCase() ?? "UNKNOWN"} ${error.config?.url ?? ""}`;
      const safePayload = payload == null ? undefined : sanitizePayloadForLogging(payload);
      if (status == null) {
        console.error(`${logPrefix} - No response received`, statusText);
      } else if (status >= 500) {
        console.error(`${logPrefix} - ${status}`, safePayload ?? statusText);
      } else {
        console.warn(`${logPrefix} - ${status}`, safePayload ?? statusText);
      }
    }

    // Handle common error scenarios
    if (status === 401) {
      // Coordinate with auth store (cookie-based auth) via a global event
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("app:auth-unauthorized"));
      }
      console.warn("Unauthorized: clearing auth state");
    } else if (status === 403) {
      console.warn("Access forbidden");
    } else if (status != null && status >= 500) {
      console.error("Server error occurred");
    }

    return Promise.reject(error);
  },
);

// Custom mutator for Orval
export const apiClient = async <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
  const response = await api(config);
  return response.data;
};

export default api;
